import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { transformRoute, useTranslate } from "helpers";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { WarehouseTireSetSearchPageCmp, WarehouseTireSetSearchPageProps } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Routes as FleetWarehouseRoutes } from "ui/modules/fleet/warehouses/navigation";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import TabMenu from "../components/TabMenu/TabMenu";
import { deleteFleetTireSetAPI, getFleetTiresSetAPI } from "services/api/fleet/tires/api";
import { Dialog } from "ui/components/Dialog";
import { FleetTireSetLocationEntityType, FleetTireSetRow } from "services/api/fleet/tires/types";
import { FleetWarehouseDetailFormValues } from "services/api/fleet/warehouses/types";
import { initialFleetWarehouseDetailForm } from "services/api/fleet/warehouses/consts";
import { getFleetWarehouseAPI } from "services/api/fleet/warehouses/api";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { SIDEBAR_ITEM_FLEET_WAREHOUSE_SEARCH } from "app/components/Sidebar/consts";
import { FLEET_WAREHOUSE_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { Button } from "ui/components/Button/Button";
import VehicleTireSetSearchResults from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleTireSetCard/components/VehicleTireSearchResults/VehicleTireSetSearchResults";
import VehicleTireSetSearchFilters from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleTireSetSearchPage/components/VehicleTireSetSearchFilters/VehicleTireSetSearchFilters";
import { FleetTireSetSearchFormValues } from "services/api/fleet/tires/types";
import { Routes as FleetTireRoutes } from "ui/modules/fleet/tires/navigation";
import TireSetEditCard from "ui/modules/fleet/tires/pages/TireSetEditCard/TireSetEditCard";
import { SIDEBAR_ITEM_FLEET_TIRE_SET_NEW } from "app/components/Sidebar/consts";

const WarehouseTireSetSearchPage: WarehouseTireSetSearchPageCmp = (
  props: WarehouseTireSetSearchPageProps
) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetWarehouseDetailFormValues>(
    initialFleetWarehouseDetailForm
  );
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetTireSetRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [tireSetId, setTireSetId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      fetchWarehouse(id);
      fetchSearchResult(id, searchResults.pagination);
    }
  }, [id]);

  const fetchWarehouse = async (id: string) => {
    setIsLoading(true);
    try {
      const result = await getFleetWarehouseAPI(id);
      if (result) setFormValues(result);
    } catch { /* */ }
    setIsLoading(false);
  };

  const fetchSearchResult = async (
    warehouseId: string,
    pagination: Pagination,
    width?: number | null,
    height?: number | null,
    radius?: number | null,
    tireManufacturerId?: string | null,
    tireTypeId?: string | null,
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetTiresSetAPI(
        pagination,
        null,
        null,
        width,
        height,
        radius,
        null,
        null,
        null,
        tireManufacturerId,
        warehouseId,
        null,
        null,
        tireTypeId,
      );
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
    }
    setIsLoading(false);
  };

  const handleSearchChange = useCallback((filterValues: FleetTireSetSearchFormValues) => {
    if (id) {
      fetchSearchResult(
        id,
        searchResults.pagination,
        filterValues.width,
        filterValues.height,
        filterValues.radius,
        filterValues.tireManufacturerId,
        filterValues.tireTypeId,
      );
    }
  }, [id, searchResults.pagination]);

  const handleRowClick = useCallback(async (tireSetId: string) => {
    navigate(
      transformRoute(FleetTireRoutes.FleetTireSetDetailPageRouter, { id: tireSetId })
    );
  }, [navigate]);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id) fetchSearchResult(id, newPagination);
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, tireSetId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setTireSetId(tireSetId);
    setOpenDialog(true);
  };

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!tireSetId) return;
    try {
      const deleted = await deleteFleetTireSetAPI(tireSetId, t);
      if (deleted && id) {
        await fetchSearchResult(id, searchResults.pagination);
      }
      setOpenDialog(false);
      setTireSetId("");
    } catch { /* */ }
  }, [tireSetId, t, id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setTireSetId("");
    setOpenDialog(false);
  }, []);

  const handleLocationClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, entityId: string, entityName: string) => {
    event.stopPropagation();
    event.preventDefault();
    let route: string;
    switch (entityName) {
      case FleetTireSetLocationEntityType.Vehicle:
        route = transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, { id: entityId });
        break;
      case FleetTireSetLocationEntityType.Warehouse:
        route = transformRoute(FleetWarehouseRoutes.FleetWarehouseDetailPageRouter, { id: entityId });
        break;
      default:
        return;
    }
    navigate(route);
  }, [navigate]);

  const handleEditClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, tireSetId: string) => {
    event.stopPropagation();
    event.preventDefault();
    navigate(
      transformRoute(FleetTireRoutes.FleetTireSetDetailPageRouter, { id: tireSetId })
    );
  }, [navigate]);

  const handleDemountClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, tireSetId: string) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  const handleAddNew = useCallback(() => {
    setShowAddNewModal(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    if (id) fetchSearchResult(id, searchResults.pagination);
    setShowAddNewModal(false);
  }, [id, searchResults.pagination]);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_FLEET_WAREHOUSE_SEARCH.text,
      route: SIDEBAR_ITEM_FLEET_WAREHOUSE_SEARCH.route,
    },
  ];

  if (!id) return <></>;

  return (
    <>
      <div>
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <div className="flex items-center justify-between w-full">
              <div>
                <PageTitle
                  title={`${t(FLEET_WAREHOUSE_DETAIL_PAGE_TITLE.text)} [${formValues.name ?? ""}]`}
                  icon={FLEET_WAREHOUSE_DETAIL_PAGE_TITLE.icon}
                  iconColor={FLEET_WAREHOUSE_DETAIL_PAGE_TITLE.iconColor}
                />
                <Breadcrumb items={breadcrumbs} />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  children={t("admin.fleet.tire.set.search.add")}
                  startIcon="/media/icons/plus.svg"
                  onClick={handleAddNew}
                />
              </div>
            </div>
          </PageHeader>
          <TabMenu warehouseId={id} />
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <VehicleTireSetSearchFilters
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.fleet.warehouse.tireset.search.noresults")} />
          ) : (
            <VehicleTireSetSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
              onLocationClick={handleLocationClick}
              onEditClick={handleEditClick}
              onDemountClick={handleDemountClick}
            />
          )}
        </div>
      </div>
      <Dialog
        open={openDialog}
        title={t("admin.fleet.tire.set.dialog.delete.title")}
        message={t("admin.fleet.tire.set.dialog.delete.message")}
        confirmText={t("admin.fleet.tire.set.dialog.delete.confirmtext")}
        cancelText={t("admin.fleet.tire.set.dialog.delete.canceltext")}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
      <TireSetEditCard
        show={showAddNewModal}
        tireSetId={null}
        modalIcon="/media/images/add-icon.svg"
        modalTitle={SIDEBAR_ITEM_FLEET_TIRE_SET_NEW.text}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
    </>
  );
};

WarehouseTireSetSearchPage.route =
  FleetWarehouseRoutes.FleetWarehouseTireSetSearchPageRouter;

export default WarehouseTireSetSearchPage;
